import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import axios from 'axios';

// ── Mini stat card ──────────────────────────────────────────
const MiniCard = ({ label, value, icon, color, bg }: any) => (
  <Card sx={{
    flex: '1 1 160px', borderRadius: '18px',
    border: '1px solid rgba(226,232,240,0.8)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)', overflow: 'hidden',
    transition: 'transform .25s,box-shadow .25s',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 10px 24px ${color}22` },
  }}>
    <Box sx={{ height: 3, background: `linear-gradient(90deg,${color},${color}88)` }} />
    <CardContent sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 0.5 }}>{label}</Typography>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-1px', lineHeight: 1 }}>{value}</Typography>
        </Box>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const STATUS_CONFIG: any = {
  paid: { color: '#10b981', bg: 'rgba(16,185,129,0.1)', label: 'PAID', chartColor: '#10b981' },
  pending: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'PENDING', chartColor: '#f59e0b' },
  unpaid: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'UNPAID', chartColor: '#ef4444' },
};

const DigiSindhPsids: React.FC = () => {
  const [psids, setPsids] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [updateLoading, setUpdateLoading] = useState<string | null>(null);

  const fetchPsids = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (courseFilter !== 'all') params.course = courseFilter;
      if (searchTerm) params.search = searchTerm;
      const API = process.env.REACT_APP_API_URL || 'https://api.hunarmandsindh.pk/api';
      const response = await axios.get(`${API}/digisindhpsidtrackings`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.data.success) setPsids(response.data.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load PSID data. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => { fetchPsids(); }, [statusFilter, courseFilter]);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); fetchPsids(); };

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      setUpdateLoading(id);
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || 'https://api.hunarmandsindh.pk/api'}/digisindhpsidtrackings/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) setPsids(psids.map(p => p._id === id ? { ...p, paymentStatus: newStatus } : p));
      setUpdateLoading(null);
    } catch (err) { setUpdateLoading(null); }
  };

  // Computed stats
  const paid = psids.filter(p => p.paymentStatus === 'paid').length;
  const pending = psids.filter(p => p.paymentStatus === 'pending').length;
  const unpaid = psids.filter(p => p.paymentStatus === 'unpaid').length;
  const chartData = [
    { name: 'Paid', value: paid },
    { name: 'Pending', value: pending },
    { name: 'Unpaid', value: unpaid },
  ].filter(d => d.value > 0);
  const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444'];

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <Box mb={3}>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-1px' }}>
          HunarmandSindh Program PSIDs
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 500, mt: 0.25 }}>
          Tracking automated PSID generation and payments for the HunarmandSindh Program
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <MiniCard label="Total PSIDs" value={psids.length.toLocaleString()} icon={<ReceiptIcon />} color="#6366f1" bg="rgba(99,102,241,0.1)" />
        <MiniCard label="Paid" value={paid.toLocaleString()} icon={<CheckCircleIcon />} color="#10b981" bg="rgba(16,185,129,0.1)" />
        <MiniCard label="Pending" value={pending.toLocaleString()} icon={<PendingIcon />} color="#f59e0b" bg="rgba(245,158,11,0.1)" />
        <MiniCard label="Unpaid" value={unpaid.toLocaleString()} icon={<CancelIcon />} color="#ef4444" bg="rgba(239,68,68,0.1)" />
      </Box>

      {/* Chart + Filters Row */}
      <Box display="flex" gap={2.5} mb={3} flexWrap="wrap">
        {/* Pie Chart */}
        {chartData.length > 0 && (
          <Card sx={{ flex: '0 0 280px', borderRadius: '20px', border: '1px solid rgba(226,232,240,0.8)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b', mb: 1 }}>Payment Distribution</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={chartData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={5} dataKey="value" stroke="none">
                    {chartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '0.82rem' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '0.78rem', fontWeight: 600 }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Filters Card */}
        <Card sx={{ flex: '1 1 300px', borderRadius: '20px', border: '1px solid rgba(226,232,240,0.8)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e293b', mb: 2 }}>Search & Filter</Typography>
            <form onSubmit={handleSearch}>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth label="Search by Name, Roll No, or Challan" variant="outlined" value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} size="small"
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 18 }} /> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                <Box display="flex" gap={1.5} flexWrap="wrap">
                  <FormControl size="small" sx={{ flex: '1 1 120px', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as string)}>
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="unpaid">Unpaid</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: '1 1 150px', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                    <InputLabel>Course</InputLabel>
                    <Select value={courseFilter} label="Course" onChange={(e) => setCourseFilter(e.target.value as string)}>
                      <MenuItem value="all">All Courses</MenuItem>
                      <MenuItem value="DigiSindh Program">HunarmandSindh Program</MenuItem>
                      <MenuItem value="Sindh Program">Sindh Program</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Button fullWidth variant="contained" type="submit" startIcon={<SearchIcon />}
                  sx={{ borderRadius: '12px', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', fontWeight: 700, py: 1.2 }}>
                  Search PSIDs
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '14px' }}>{error}</Alert>}

      {/* Table */}
      <Card sx={{ borderRadius: '20px', border: '1px solid rgba(226,232,240,0.8)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['Challan No', 'Student Name', 'Roll Number', 'Course', 'Status', 'Update Status'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.73rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '2px solid #e2e8f0', py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={36} sx={{ color: '#6366f1' }} />
                    <Typography sx={{ mt: 2, color: '#94a3b8', fontSize: '0.85rem' }}>Loading HunarmandSindh PSID data...</Typography>
                  </TableCell>
                </TableRow>
              ) : psids.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '18px', bgcolor: 'rgba(99,102,241,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                      <ReceiptIcon sx={{ color: '#6366f1', fontSize: 28 }} />
                    </Box>
                    <Typography sx={{ color: '#94a3b8', fontWeight: 600 }}>No PSIDs found matching your criteria.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                psids.map((row) => {
                  const cfg = STATUS_CONFIG[row.paymentStatus] || STATUS_CONFIG['unpaid'];
                  return (
                    <TableRow key={row._id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' }, transition: 'background .15s' }}>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>{row.challanNumber}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#6366f1', fontSize: '0.75rem', fontWeight: 700 }}>
                            {row.studentName ? row.studentName.charAt(0).toUpperCase() : '?'}
                          </Avatar>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{row.studentName || 'N/A'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontFamily: 'monospace' }}>{row.studentRollNumber || 'N/A'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.course || 'HunarmandSindh Program'} size="small"
                          sx={{ fontSize: '0.72rem', fontWeight: 600, bgcolor: 'rgba(99,102,241,0.08)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.18)' }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={cfg.label} size="small"
                          sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }} />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.82rem' } }}>
                          <Select value={row.paymentStatus} onChange={(e) => handleStatusUpdate(row._id, e.target.value as string)} disabled={updateLoading === row._id}>
                            <MenuItem value="unpaid">Unpaid</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default DigiSindhPsids;
